import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { getDatabase, onValue, ref as rtdbRef, remove as rtdbRemove, update as rtdbUpdate } from 'firebase/database';
import {
  AlertTriangle,
  BarChart3,
  Bell,
  Ban,
  CheckCircle,
  ClipboardList,
  Crown,
  Eye,
  Flag,
  Gavel,
  MessageSquare,
  Package,
  RotateCcw,
  Search,
  Shield,
  Store,
  Trash2,
  TrendingUp,
  Users,
  MessagesSquare   // <-- added for chats tab icon
} from 'lucide-react';
import { FaSpinner } from 'react-icons/fa';
import { db } from '../../firebase/firebase';
import { PLANS } from '../../firebase/plans';
import './AdminDashboard.css';

const planOptions = Object.keys(PLANS);
const productStatuses = ['active', 'pending', 'suspended', 'sold'];
const auctionStatuses = ['active', 'suspended', 'closed', 'cancelled'];
const preorderStatuses = ['pending', 'confirmed', 'completed', 'cancelled'];

const statConfig = [
  { key: 'users', label: 'Users', icon: Users },
  { key: 'products', label: 'Products', icon: Package },
  { key: 'auctions', label: 'Auctions', icon: Gavel },
  { key: 'preorders', label: 'Pre-orders', icon: ClipboardList },
  { key: 'posts', label: 'Community posts', icon: MessageSquare },
  { key: 'reports', label: 'Reports', icon: AlertTriangle },
  { key: 'chats', label: 'Conversations', icon: MessagesSquare }, // now displayed
];

const AdminDashboard = () => {
  // --- Core data states ---
  const [stats, setStats] = useState({ users: 0, products: 0, auctions: 0, posts: 0, reports: 0, chats: 0, preorders: 0 });
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [reports, setReports] = useState([]);
  const [chats, setChats] = useState([]);        // NOW USED in UI
  const [products, setProducts] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [preorders, setPreorders] = useState([]);

  // --- UI states ---
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [broadcast, setBroadcast] = useState({ title: '', message: '' });
  const [actionLoading, setActionLoading] = useState({});

  // --- Loading & error states per collection (Firestore) ---
  const [loadingFlags, setLoadingFlags] = useState({
    users: true,
    posts: true,
    reports: true,
    chats: true,
    auctions: true,
    preorders: true
  });
  const [firestoreErrors, setFirestoreErrors] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(true);

  // --- Helper: Write audit log ---
  const writeAudit = async (action, targetType, targetId, details = {}) => {
    try {
      await addDoc(collection(db, 'adminAuditLogs'), {
        action,
        targetType,
        targetId,
        details,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Audit log failed:', error);
    }
  };

  // --- FAULT‑TOLERANT FIRESTORE LOADER (includes chats) ---
  const loadFirestoreData = async () => {
    const loadCollection = async (name, setter, statKey) => {
      setLoadingFlags(prev => ({ ...prev, [name]: true }));
      try {
        const snapshot = await getDocs(query(collection(db, name), limit(500)));
        const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setter(items);
        setStats(prev => ({ ...prev, [statKey]: items.length }));
        setFirestoreErrors(prev => ({ ...prev, [name]: null }));
        console.log(`✅ Firestore collection "${name}" loaded: ${items.length} items`);
      } catch (error) {
        console.error(`❌ Firestore collection "${name}" failed:`, error);
        setter([]);
        setStats(prev => ({ ...prev, [statKey]: 0 }));
        setFirestoreErrors(prev => ({ ...prev, [name]: error.message }));
      } finally {
        setLoadingFlags(prev => ({ ...prev, [name]: false }));
      }
    };

    await Promise.allSettled([
      loadCollection('users', setUsers, 'users'),
      loadCollection('posts', setPosts, 'posts'),
      loadCollection('reports', setReports, 'reports'),
      loadCollection('chats', setChats, 'chats'),     // <-- included
      loadCollection('auctions', setAuctions, 'auctions'),
      loadCollection('pre-orders', setPreorders, 'preorders')
    ]);
  };

  // --- Products (RTDB) listener ---
  useEffect(() => {
    const database = getDatabase();
    const productsRef = rtdbRef(database, 'products');
    const unsubscribeProducts = onValue(productsRef, (snapshot) => {
      const productRows = [];
      snapshot.forEach((child) => {
        productRows.push({ id: child.key, ...child.val() });
      });
      productRows.sort((a, b) => (Number(b.rank || 0) - Number(a.rank || 0)) || Number(b.createdAt || 0) - Number(a.createdAt || 0));
      setProducts(productRows);
      setStats((current) => ({ ...current, products: productRows.length }));
      setLoadingProducts(false);
    }, (error) => {
      console.error('❌ RTDB products failed to load:', error);
      setLoadingProducts(false);
    });

    return () => unsubscribeProducts();
  }, []);

  // Initial Firestore load
  useEffect(() => {
    loadFirestoreData();
  }, []);

  // --- Admin action helpers (all preserved, reload fault‑tolerant) ---
  const updateUser = async (userId, payload, action = 'update_user') => {
    setActionLoading(prev => ({ ...prev, [`${action}_${userId}`]: true }));
    try {
      await updateDoc(doc(db, 'users', userId), {
        ...payload,
        adminUpdatedAt: serverTimestamp()
      });
      await writeAudit(action, 'user', userId, payload);
      await loadFirestoreData();
    } catch (error) {
      console.error(`Failed to update user ${userId}:`, error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${action}_${userId}`]: false }));
    }
  };

  const updateUserPlanFromAdmin = async (userId, planId) => {
    const plan = PLANS[planId] || PLANS.free;
    await updateUser(userId, {
      plan: plan.id,
      planData: {
        ...plan,
        listableProducts: Number.isFinite(plan.listableProducts) ? plan.listableProducts : -1,
        assignedByAdmin: true,
        assignedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + plan.duration * 24 * 60 * 60 * 1000).toISOString()
      }
    }, 'change_user_plan');
  };

  const updatePost = async (postId, payload, action = 'update_post') => {
    setActionLoading(prev => ({ ...prev, [`${action}_${postId}`]: true }));
    try {
      await updateDoc(doc(db, 'posts', postId), {
        ...payload,
        adminUpdatedAt: serverTimestamp()
      });
      await writeAudit(action, 'post', postId, payload);
      await loadFirestoreData();
    } catch (error) {
      console.error(`Failed to update post ${postId}:`, error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${action}_${postId}`]: false }));
    }
  };

  const updateReport = async (reportId, payload, action = 'update_report') => {
    setActionLoading(prev => ({ ...prev, [`${action}_${reportId}`]: true }));
    try {
      await updateDoc(doc(db, 'reports', reportId), {
        ...payload,
        reviewedAt: serverTimestamp()
      });
      await writeAudit(action, 'report', reportId, payload);
      await loadFirestoreData();
    } catch (error) {
      console.error(`Failed to update report ${reportId}:`, error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${action}_${reportId}`]: false }));
    }
  };

  const updateAuction = async (auctionId, payload, action = 'update_auction') => {
    setActionLoading(prev => ({ ...prev, [`${action}_${auctionId}`]: true }));
    try {
      await updateDoc(doc(db, 'auctions', auctionId), {
        ...payload,
        adminUpdatedAt: serverTimestamp()
      });
      await writeAudit(action, 'auction', auctionId, payload);
      await loadFirestoreData();
    } catch (error) {
      console.error(`Failed to update auction ${auctionId}:`, error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${action}_${auctionId}`]: false }));
    }
  };

  const updatePreorder = async (preorderId, payload, action = 'update_preorder') => {
    setActionLoading(prev => ({ ...prev, [`${action}_${preorderId}`]: true }));
    try {
      await updateDoc(doc(db, 'pre-orders', preorderId), {
        ...payload,
        adminUpdatedAt: serverTimestamp()
      });
      await writeAudit(action, 'preorder', preorderId, payload);
      await loadFirestoreData();
    } catch (error) {
      console.error(`Failed to update preorder ${preorderId}:`, error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${action}_${preorderId}`]: false }));
    }
  };

  const deleteAuction = async (auctionId) => {
    if (!window.confirm('Delete this auction permanently?')) return;
    setActionLoading(prev => ({ ...prev, [`delete_${auctionId}`]: true }));
    try {
      await deleteDoc(doc(db, 'auctions', auctionId));
      await writeAudit('delete_auction', 'auction', auctionId);
      await loadFirestoreData();
    } catch (error) {
      console.error(`Failed to delete auction ${auctionId}:`, error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete_${auctionId}`]: false }));
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Delete this report permanently?')) return;
    setActionLoading(prev => ({ ...prev, [`delete_report_${reportId}`]: true }));
    try {
      await deleteDoc(doc(db, 'reports', reportId));
      await writeAudit('delete_report', 'report', reportId);
      await loadFirestoreData();
    } catch (error) {
      console.error(`Failed to delete report ${reportId}:`, error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete_report_${reportId}`]: false }));
    }
  };

  // --- NEW: Delete chat conversation ---
  const deleteChat = async (chatId) => {
    if (!window.confirm('Delete this entire conversation permanently? This action cannot be undone.')) return;
    setActionLoading(prev => ({ ...prev, [`delete_chat_${chatId}`]: true }));
    try {
      await deleteDoc(doc(db, 'chats', chatId));
      await writeAudit('delete_chat', 'chat', chatId);
      await loadFirestoreData();
    } catch (error) {
      console.error(`Failed to delete chat ${chatId}:`, error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete_chat_${chatId}`]: false }));
    }
  };

  // --- RTDB product actions (unchanged) ---
  const updateProduct = async (productId, payload, action = 'update_product') => {
    setActionLoading(prev => ({ ...prev, [`${action}_${productId}`]: true }));
    try {
      const database = getDatabase();
      await rtdbUpdate(rtdbRef(database, `products/${productId}`), {
        ...payload,
        adminUpdatedAt: Date.now()
      });
      await writeAudit(action, 'product', productId, payload);
    } catch (error) {
      console.error(`Failed to update product ${productId}:`, error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${action}_${productId}`]: false }));
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Delete this product listing permanently?')) return;
    setActionLoading(prev => ({ ...prev, [`delete_${productId}`]: true }));
    try {
      const database = getDatabase();
      await rtdbRemove(rtdbRef(database, `products/${productId}`));
      await writeAudit('delete_product', 'product', productId);
    } catch (error) {
      console.error(`Failed to delete product ${productId}:`, error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete_${productId}`]: false }));
    }
  };

  const handleBroadcast = async () => {
    if (!broadcast.title.trim() || !broadcast.message.trim()) {
      alert('Add both a title and message.');
      return;
    }
    try {
      await addDoc(collection(db, 'broadcasts'), {
        title: broadcast.title.trim(),
        message: broadcast.message.trim(),
        type: 'admin_broadcast',
        createdAt: serverTimestamp(),
        readBy: []
      });
      await writeAudit('broadcast_notification', 'broadcast', 'all', broadcast);
      setBroadcast({ title: '', message: '' });
      alert('Broadcast created.');
    } catch (error) {
      console.error('Failed to send broadcast:', error);
      alert('Broadcast failed. See console.');
    }
  };

  // --- Filtered data (including chats) ---
  const queryText = searchQuery.toLowerCase().trim();

  const filteredUsers = useMemo(() => users.filter((user) => (
    !queryText ||
    user.displayName?.toLowerCase().includes(queryText) ||
    user.fullName?.toLowerCase().includes(queryText) ||
    user.name?.toLowerCase().includes(queryText) ||
    user.email?.toLowerCase().includes(queryText) ||
    user.username?.toLowerCase().includes(queryText)
  )), [users, queryText]);

  const filteredProducts = useMemo(() => products.filter((product) => (
    !queryText ||
    product.name?.toLowerCase().includes(queryText) ||
    product.category?.toLowerCase().includes(queryText) ||
    product.location?.toLowerCase().includes(queryText) ||
    product.sellerId?.toLowerCase().includes(queryText)
  )), [products, queryText]);

  const filteredPosts = useMemo(() => posts.filter((post) => (
    !queryText ||
    post.text?.toLowerCase().includes(queryText) ||
    post.userId?.toLowerCase().includes(queryText)
  )), [posts, queryText]);

  const filteredAuctions = useMemo(() => auctions.filter((auction) => (
    !queryText ||
    auction.productName?.toLowerCase().includes(queryText) ||
    auction.sellerId?.toLowerCase().includes(queryText) ||
    auction.highestBidderName?.toLowerCase().includes(queryText)
  )), [auctions, queryText]);

  const filteredPreorders = useMemo(() => preorders.filter((preorder) => (
    !queryText ||
    preorder.customerName?.toLowerCase().includes(queryText) ||
    preorder.mobileNumber?.toLowerCase().includes(queryText) ||
    preorder.deliveryAddress?.toLowerCase().includes(queryText) ||
    preorder.customerId?.toLowerCase().includes(queryText) ||
    preorder.orderDetails?.some((item) => (
      item.plantName?.toLowerCase().includes(queryText) ||
      item.varietyName?.toLowerCase().includes(queryText)
    ))
  )), [preorders, queryText]);

  // New: filter chats by participant name / last message
  const filteredChats = useMemo(() => chats.filter((chat) => {
    if (!queryText) return true;
    const participants = (chat.participants || []).join(' ').toLowerCase();
    const lastMsg = (chat.lastMessage || '').toLowerCase();
    const productName = (chat.productName || '').toLowerCase();
    return participants.includes(queryText) || lastMsg.includes(queryText) || productName.includes(queryText);
  }), [chats, queryText]);

  const formatAdminDate = (value) => {
    if (!value) return 'N/A';
    const date = value?.toDate ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper: render loading/error inside a section
  const renderCollectionStatus = (collectionName, isLoading, error) => {
    if (isLoading) return <div className="admin-loading"><FaSpinner className="spin" /> Loading {collectionName}...</div>;
    if (error) return <div className="admin-error"><AlertTriangle size={16} /> Failed to load {collectionName}: {error}</div>;
    return null;
  };

  const renderSearch = (placeholder) => (
    <div className="search-box">
      <Search size={18} />
      <input
        type="text"
        placeholder={placeholder}
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
      />
    </div>
  );

  // --- Existing render functions (unchanged except minor fixes) ---
  const renderOverview = () => (
    <>
      <section className="admin-hero">
        <div>
          <span><Shield size={16} /> Admin</span>
          <h1>Platform control center</h1>
          <p>Manage users, memberships, listings, product ranking, reports, community moderation, broadcasts, and abuse controls.</p>
        </div>
      </section>

      <section className="admin-grid" aria-label="Admin metrics">
        {statConfig.map(({ key, label, icon: Icon }) => {
          const isLoading = key === 'products' ? loadingProducts : loadingFlags[key === 'preorders' ? 'preorders' : key];
          const error = key !== 'products' ? firestoreErrors[key === 'preorders' ? 'pre-orders' : key] : null;
          return (
            <article className="admin-stat" key={key}>
              <Icon size={22} />
              <span>{label}</span>
              <strong>
                {isLoading ? '...' : error ? '⚠️' : stats[key]}
              </strong>
            </article>
          );
        })}
      </section>

      <section className="admin-panel">
        <div>
          <h2><Bell size={20} /> Broadcast notification</h2>
          <p>Create an admin broadcast document. Wire this into notification listeners to show platform-wide alerts.</p>
        </div>
        <div className="admin-form-stack">
          <input value={broadcast.title} onChange={(event) => setBroadcast((current) => ({ ...current, title: event.target.value }))} placeholder="Broadcast title" />
          <textarea value={broadcast.message} onChange={(event) => setBroadcast((current) => ({ ...current, message: event.target.value }))} placeholder="Broadcast message" rows="3" />
          <button className="admin-primary-action" onClick={handleBroadcast}>Send broadcast</button>
        </div>
      </section>
    </>
  );

  const renderUserManagement = () => (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2><Users size={20} /> User Management</h2>
        {renderSearch('Search users...')}
      </div>
      {renderCollectionStatus('users', loadingFlags.users, firestoreErrors.users)}
      {!loadingFlags.users && !firestoreErrors.users && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead><tr><th>User</th><th>Email</th><th>Plan</th><th>Status</th><th>Trust</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td><strong>{user.fullName || user.displayName || user.name || 'Unknown'}</strong><span className="admin-subtext">@{user.username || user.id.slice(0, 8)}</span></td>
                  <td>{user.email || 'N/A'}</td>
                  <td><select value={user.plan || 'free'} onChange={(event) => updateUserPlanFromAdmin(user.id, event.target.value)}>{planOptions.map((plan) => <option key={plan} value={plan}>{plan}</option>)}</select></td>
                  <td><span className={`status-badge ${user.suspended ? 'suspended' : 'active'}`}>{user.suspended ? 'Suspended' : 'Active'}</span></td>
                  <td><div className="admin-inline-actions"><label><input type="checkbox" checked={Boolean(user.isVerified)} onChange={(event) => updateUser(user.id, { isVerified: event.target.checked }, 'toggle_user_verified')} /> Verified</label><label><input type="checkbox" checked={Boolean(user.isTrusted)} onChange={(event) => updateUser(user.id, { isTrusted: event.target.checked }, 'toggle_user_trusted')} /> Trusted</label></div></td>
                  <td><div className="action-buttons">{user.suspended ? (<button className="action-success" onClick={() => updateUser(user.id, { suspended: false, suspensionReason: '' }, 'unsuspend_user')} disabled={actionLoading[`unsuspend_user_${user.id}`]}>{actionLoading[`unsuspend_user_${user.id}`] ? <FaSpinner className="spin" size={16} /> : <RotateCcw size={16} />}</button>) : (<button className="action-danger" onClick={() => updateUser(user.id, { suspended: true, suspensionReason: 'Admin action' }, 'suspend_user')} disabled={actionLoading[`suspend_user_${user.id}`]}>{actionLoading[`suspend_user_${user.id}`] ? <FaSpinner className="spin" size={16} /> : <Ban size={16} />}</button>)}<button className={user.sellerVerified ? 'action-success' : 'action-warning'} onClick={() => updateUser(user.id, { sellerVerified: !user.sellerVerified }, 'toggle_seller_verified')} disabled={actionLoading[`toggle_seller_verified_${user.id}`]}>{actionLoading[`toggle_seller_verified_${user.id}`] ? <FaSpinner className="spin" size={16} /> : <Crown size={16} />}</button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderProductControls = () => (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2><Package size={20} /> Product Controls</h2>
        {renderSearch('Search products, sellers, locations...')}
      </div>
      {loadingProducts && <div className="admin-loading"><FaSpinner className="spin" /> Loading products from Realtime Database...</div>}
      {!loadingProducts && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead><tr><th>Product</th><th>Seller</th><th>Price</th><th>Status</th><th>Rank</th><th>Boost</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id}>
                  <td><strong>{product.name || 'Unnamed product'}</strong><span className="admin-subtext">{product.category || 'Uncategorized'} · {product.location || 'No location'}</span></td>
                  <td>{product.sellerId || 'Unknown'}</td>
                  <td>₹{Number(product.price || 0).toLocaleString('en-IN')}</td>
                  <td><select value={product.status || 'active'} onChange={(event) => updateProduct(product.id, { status: event.target.value }, 'change_product_status')}>{productStatuses.map((status) => <option key={status} value={status}>{status}</option>)}</select></td>
                  <td><input className="rank-input" type="number" value={product.rank || 0} onChange={(event) => updateProduct(product.id, { rank: Number(event.target.value) || 0 }, 'rank_product')} /></td>
                  <td><label className="admin-checkbox"><input type="checkbox" checked={Boolean(product.adminPinned)} onChange={(event) => updateProduct(product.id, { adminPinned: event.target.checked }, 'toggle_product_pin')} /> Pinned</label></td>
                  <td><div className="action-buttons"><button className={product.status === 'suspended' ? 'action-success' : 'action-danger'} onClick={() => updateProduct(product.id, { status: product.status === 'suspended' ? 'active' : 'suspended' }, 'toggle_product_suspended')} disabled={actionLoading[`toggle_product_suspended_${product.id}`]}>{actionLoading[`toggle_product_suspended_${product.id}`] ? <FaSpinner className="spin" size={16} /> : product.status === 'suspended' ? <RotateCcw size={16} /> : <Ban size={16} />}</button><button className="action-danger" onClick={() => deleteProduct(product.id)} disabled={actionLoading[`delete_${product.id}`]}>{actionLoading[`delete_${product.id}`] ? <FaSpinner className="spin" size={16} /> : <Trash2 size={16} />}</button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderAuctionControls = () => (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2><Gavel size={20} /> Bidding Controls</h2>
        {renderSearch('Search auctions, sellers, bidders...')}
      </div>
      {renderCollectionStatus('auctions', loadingFlags.auctions, firestoreErrors.auctions)}
      {!loadingFlags.auctions && !firestoreErrors.auctions && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead><tr><th>Auction</th><th>Seller</th><th>Current bid</th><th>Bid count</th><th>Status</th><th>Winner</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredAuctions.map((auction) => (
                <tr key={auction.id}>
                  <td><strong>{auction.productName || 'Unnamed auction'}</strong><span className="admin-subtext">{auction.description || 'No description'}</span></td>
                  <td>{auction.sellerId || 'Unknown'}</td>
                  <td>₹{Number(auction.currentBid || auction.startingBid || 0).toLocaleString('en-IN')}</td>
                  <td>{auction.bidCount || 0}</td>
                  <td><select value={auction.status || 'active'} onChange={(event) => updateAuction(auction.id, { status: event.target.value }, 'change_auction_status')}>{auctionStatuses.map((status) => <option key={status} value={status}>{status}</option>)}</select></td>
                  <td>{auction.highestBidderName || 'No bids'}</td>
                  <td><div className="action-buttons"><button className={auction.status === 'suspended' ? 'action-success' : 'action-danger'} onClick={() => updateAuction(auction.id, { status: auction.status === 'suspended' ? 'active' : 'suspended' }, 'toggle_auction_suspended')} disabled={actionLoading[`toggle_auction_suspended_${auction.id}`]}>{actionLoading[`toggle_auction_suspended_${auction.id}`] ? <FaSpinner className="spin" size={16} /> : auction.status === 'suspended' ? <RotateCcw size={16} /> : <Ban size={16} />}</button><button className="action-success" onClick={() => updateAuction(auction.id, { status: 'closed', closedAt: serverTimestamp() }, 'close_auction')} disabled={actionLoading[`close_auction_${auction.id}`]}>{actionLoading[`close_auction_${auction.id}`] ? <FaSpinner className="spin" size={16} /> : <CheckCircle size={16} />}</button><button className="action-danger" onClick={() => deleteAuction(auction.id)} disabled={actionLoading[`delete_${auction.id}`]}>{actionLoading[`delete_${auction.id}`] ? <FaSpinner className="spin" size={16} /> : <Trash2 size={16} />}</button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderPreorderControls = () => (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2><ClipboardList size={20} /> Pre-Orders</h2>
        {renderSearch('Search preorders, customers, addresses...')}
      </div>
      {renderCollectionStatus('pre-orders', loadingFlags.preorders, firestoreErrors['pre-orders'])}
      {!loadingFlags.preorders && !firestoreErrors['pre-orders'] && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead><tr><th>Customer</th><th>Mobile</th><th>Address</th><th>Delivery Date</th><th>Items</th><th>Meta</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredPreorders.map((preorder) => (
                <tr key={preorder.id}>
                  <td><strong>{preorder.customerName || 'Unknown'}</strong><span className="admin-subtext">ID: {preorder.customerId || 'N/A'}</span><span className="admin-subtext">Created: {formatAdminDate(preorder.createdAt)}</span></td>
                  <td>{preorder.mobileNumber || 'N/A'}</td>
                  <td className="content-cell">{preorder.deliveryAddress || 'N/A'}</td>
                  <td>{preorder.deliveryDate ? new Date(preorder.deliveryDate).toLocaleDateString() : 'N/A'}</td>
                  <td><div className="preorder-items">{(preorder.orderDetails || []).map((item, index) => (<div key={`${preorder.id}-${index}`}><strong>{item.plantName || 'Plant'}</strong><span>{item.varietyName || 'Variety'} · Qty {item.quantity || 1} · ₹{Number(item.proposedPrice || 0).toLocaleString('en-IN')}</span></div>))}{( (!preorder.orderDetails || preorder.orderDetails.length === 0) && <span className="admin-subtext">No items saved</span>)}</div></td>
                  <td><span className="admin-subtext">Share info: {preorder.shareInfo ? 'Yes' : 'No'}</span><span className="admin-subtext">Supplier: {preorder.supplierId || 'Unassigned'}</span><span className="admin-subtext">Tracking: {preorder.trackingInfo || 'N/A'}</span><span className="admin-subtext">Notes: {preorder.supplierNotes || 'N/A'}</span></td>
                  <td><select value={preorder.status || 'pending'} onChange={(event) => updatePreorder(preorder.id, { status: event.target.value }, 'change_preorder_status')}>{preorderStatuses.map((status) => <option key={status} value={status}>{status}</option>)}</select></td>
                  <td><div className="action-buttons"><button className={preorder.status === 'pending' ? 'action-success' : 'action-warning'} onClick={() => updatePreorder(preorder.id, { status: preorder.status === 'pending' ? 'confirmed' : 'pending' }, 'toggle_preorder_status')} disabled={actionLoading[`toggle_preorder_status_${preorder.id}`]}>{actionLoading[`toggle_preorder_status_${preorder.id}`] ? <FaSpinner className="spin" size={16} /> : preorder.status === 'pending' ? <CheckCircle size={16} /> : <RotateCcw size={16} />}</button><button className="action-success" onClick={() => updatePreorder(preorder.id, { status: 'completed' }, 'complete_preorder')} disabled={actionLoading[`complete_preorder_${preorder.id}`]}>{actionLoading[`complete_preorder_${preorder.id}`] ? <FaSpinner className="spin" size={16} /> : <CheckCircle size={16} />}</button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderCommunityModeration = () => (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2><MessageSquare size={20} /> Community Moderation</h2>
        {renderSearch('Search posts...')}
      </div>
      {renderCollectionStatus('posts', loadingFlags.posts, firestoreErrors.posts)}
      {!loadingFlags.posts && !firestoreErrors.posts && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead><tr><th>Author</th><th>Content</th><th>Status</th><th>Reports</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredPosts.map((post) => (
                <tr key={post.id}>
                  <td>{post.userId || 'Unknown'}</td>
                  <td className="content-cell">{post.text || 'N/A'}</td>
                  <td><span className={`status-badge ${post.hidden ? 'hidden' : 'visible'}`}>{post.hidden ? 'Hidden' : 'Visible'}</span></td>
                  <td>{post.reportsCount || 0}</td>
                  <td><div className="action-buttons"><button className={post.hidden ? 'action-success' : 'action-warning'} onClick={() => updatePost(post.id, { hidden: !post.hidden }, post.hidden ? 'unhide_post' : 'hide_post')} disabled={actionLoading[`${post.hidden ? 'unhide_post' : 'hide_post'}_${post.id}`]}>{actionLoading[`${post.hidden ? 'unhide_post' : 'hide_post'}_${post.id}`] ? <FaSpinner className="spin" size={16} /> : post.hidden ? <RotateCcw size={16} /> : <Eye size={16} />}</button><button className={post.pinned ? 'action-success' : 'action-warning'} onClick={() => updatePost(post.id, { pinned: !post.pinned }, 'toggle_post_pin')} disabled={actionLoading[`toggle_post_pin_${post.id}`]}>{actionLoading[`toggle_post_pin_${post.id}`] ? <FaSpinner className="spin" size={16} /> : <TrendingUp size={16} />}</button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderReports = () => (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2><AlertTriangle size={20} /> Reports</h2>
      </div>
      {renderCollectionStatus('reports', loadingFlags.reports, firestoreErrors.reports)}
      {!loadingFlags.reports && !firestoreErrors.reports && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead><tr><th>Reporter</th><th>Type</th><th>Description</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id}>
                  <td>{report.reporterId || report.userId || 'Unknown'}</td>
                  <td>{report.type || 'General'}</td>
                  <td className="content-cell">{report.description || report.reason || 'N/A'}</td>
                  <td><span className={`status-badge ${report.status === 'resolved' ? 'active' : 'suspended'}`}>{report.status || 'open'}</span></td>
                  <td><div className="action-buttons"><button className="action-success" onClick={() => updateReport(report.id, { status: 'resolved' }, 'resolve_report')} disabled={actionLoading[`resolve_report_${report.id}`]}>{actionLoading[`resolve_report_${report.id}`] ? <FaSpinner className="spin" size={16} /> : <CheckCircle size={16} />}</button><button className="action-warning" onClick={() => updateReport(report.id, { status: 'open' }, 'reopen_report')} disabled={actionLoading[`reopen_report_${report.id}`]}>{actionLoading[`reopen_report_${report.id}`] ? <FaSpinner className="spin" size={16} /> : <Flag size={16} />}</button><button className="action-danger" onClick={() => handleDeleteReport(report.id)} disabled={actionLoading[`delete_report_${report.id}`]}>{actionLoading[`delete_report_${report.id}`] ? <FaSpinner className="spin" size={16} /> : <Trash2 size={16} />}</button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // --- NEW: Chat Management Renderer ---
  const renderChatManagement = () => (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2><MessagesSquare size={20} /> Chat Conversations</h2>
        {renderSearch('Search by participant, product, or last message...')}
      </div>
      {renderCollectionStatus('chats', loadingFlags.chats, firestoreErrors.chats)}
      {!loadingFlags.chats && !firestoreErrors.chats && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Participants</th>
                <th>Product</th>
                <th>Last Message</th>
                <th>Last Updated</th>
                <th>Message Count</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredChats.map((chat) => (
                <tr key={chat.id}>
                  <td>
                    <strong>{(chat.participants || []).join(', ')}</strong>
                    <span className="admin-subtext">Chat ID: {chat.id.slice(0, 8)}</span>
                  </td>
                  <td>{chat.productName || 'General chat'}</td>
                  <td className="content-cell">{chat.lastMessage || 'No messages'}</td>
                  <td>{formatAdminDate(chat.updatedAt || chat.createdAt)}</td>
                  <td>{chat.messageCount || 0}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-danger"
                        onClick={() => deleteChat(chat.id)}
                        disabled={actionLoading[`delete_chat_${chat.id}`]}
                        title="Delete entire conversation"
                      >
                        {actionLoading[`delete_chat_${chat.id}`] ? <FaSpinner className="spin" size={16} /> : <Trash2 size={16} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredChats.length === 0 && !loadingFlags.chats && (
                <tr><td colSpan="6" className="admin-empty-state">No conversations found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderSecurity = () => (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2><Shield size={20} /> Security Dashboard</h2>
      </div>
      <div className="security-grid">
        <div className="security-card"><Ban size={24} /><h3>Suspended users</h3><p>Accounts currently blocked by admin controls</p><span className="security-count">{users.filter((user) => user.suspended).length}</span></div>
        <div className="security-card"><AlertTriangle size={24} /><h3>Open reports</h3><p>Reports waiting for moderation</p><span className="security-count">{reports.filter((report) => report.status !== 'resolved').length}</span></div>
        <div className="security-card"><Package size={24} /><h3>Suspended products</h3><p>Listings removed from active marketplace visibility</p><span className="security-count">{products.filter((product) => product.status === 'suspended').length}</span></div>
        <div className="security-card"><Shield size={24} /><h3>Admin actions</h3><p>Audit logs are written to Firestore for every control action</p><span className="security-count">On</span></div>
      </div>
    </div>
  );

  return (
    <main className="admin-shell">
      <div className="admin-tabs">
        <button className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}><BarChart3 size={18} /> Overview</button>
        <button className={`tab-button ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}><Users size={18} /> Users</button>
        <button className={`tab-button ${activeTab === 'products' ? 'active' : ''}`} onClick={() => setActiveTab('products')}><Package size={18} /> Products</button>
        <button className={`tab-button ${activeTab === 'auctions' ? 'active' : ''}`} onClick={() => setActiveTab('auctions')}><Gavel size={18} /> Bidding</button>
        <button className={`tab-button ${activeTab === 'preorders' ? 'active' : ''}`} onClick={() => setActiveTab('preorders')}><ClipboardList size={18} /> Pre-Orders</button>
        <button className={`tab-button ${activeTab === 'community' ? 'active' : ''}`} onClick={() => setActiveTab('community')}><MessageSquare size={18} /> Community</button>
        <button className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}><AlertTriangle size={18} /> Reports</button>
        <button className={`tab-button ${activeTab === 'chats' ? 'active' : ''}`} onClick={() => setActiveTab('chats')}><MessagesSquare size={18} /> Chats</button> {/* NEW TAB */}
        <button className={`tab-button ${activeTab === 'security' ? 'active' : ''}`} onClick={() => setActiveTab('security')}><Shield size={18} /> Security</button>
      </div>

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'users' && renderUserManagement()}
      {activeTab === 'products' && renderProductControls()}
      {activeTab === 'auctions' && renderAuctionControls()}
      {activeTab === 'preorders' && renderPreorderControls()}
      {activeTab === 'community' && renderCommunityModeration()}
      {activeTab === 'reports' && renderReports()}
      {activeTab === 'chats' && renderChatManagement()}   {/* NEW */}
      {activeTab === 'security' && renderSecurity()}
    </main>
  );
};

export default AdminDashboard;
